import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  WorkOutline,
  SearchOutlined,
  LocationOnOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  BookmarkBorderOutlined
} from '@mui/icons-material';
import { fetchSavedJobs, fetchAppliedJobs, getCounters } from '../../../../actions/jobs';
import { jobsPageSize } from '../../../../utils/constants';
import DashboardCard, { CardHeader, StatusPill, EmptyState } from '../DashboardCard/DashboardCard';

function Tab({ label, count, active, loading, onClick }) {
  return (
    <button
      onClick={onClick}
      className={[
        'flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[13px] font-semibold',
        'border cursor-pointer transition-all duration-150',
        active
          ? 'bg-[#0094B3] text-white border-[#0094B3]'
          : 'bg-white text-[#6B7A85] border-[#E2E8ED] hover:border-[#0094B3] hover:text-[#0094B3]'
      ].join(' ')}
    >
      {label}
      {loading ? (
        <span
          className={`w-4 h-4 rounded-full border-2 animate-spin border-t-transparent ${
            active ? 'border-white' : 'border-[#6B7A85]'
          }`}
        />
      ) : (
        <span
          className={[
            'flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold',
            active ? 'bg-white/20 text-white' : 'bg-[#F0F4F7] text-[#6B7A85]'
          ].join(' ')}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center px-5 py-4 border-b border-[#E2E8ED] gap-3 animate-pulse">
      <div className="flex-1 min-w-0">
        <div className="h-3.5 bg-[#E2E8ED] rounded w-48 mb-2" />
        <div className="flex gap-3">
          <div className="h-3 bg-[#E2E8ED] rounded w-20" />
          <div className="h-3 bg-[#E2E8ED] rounded w-24" />
        </div>
      </div>
      <div className="h-5 bg-[#E2E8ED] rounded-full w-20 shrink-0" />
    </div>
  );
}

function JobRow({ job, tab, onClick }) {
  const locationStr = job.City && job.Country ? `${job.City}, ${job.Country}` : job.Location ?? '';

  const dateLabel = tab === 'applied' ? job.ApplicationDate : job.SavedDate;

  const statusStr = tab === 'applied' ? job.ApplicationStatus ?? 'Under Review' : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) onClick(e);
      }}
      className="flex items-center gap-3 px-5 py-4 border-b border-[#E2E8ED] last:border-b-0 cursor-pointer hover:bg-[#F8FAFB] transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-semibold text-[#022D41] truncate group-hover:text-[#0094B3] transition-colors">
          {job.JobTitle}
        </div>
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
          {job.CompanyName && (
            <span className="flex items-center gap-1 text-[13px] text-[#6B7A85]">
              <BusinessOutlined sx={{ fontSize: 13 }} />
              {job.CompanyName}
            </span>
          )}
          {locationStr && (
            <span className="flex items-center gap-1 text-[13px] text-[#6B7A85]">
              <LocationOnOutlined sx={{ fontSize: 13 }} />
              {locationStr}
            </span>
          )}
          {dateLabel && (
            <span className="flex items-center gap-1 text-[13px] text-[#6B7A85]">
              <CalendarTodayOutlined sx={{ fontSize: 13 }} />
              {tab === 'applied' ? 'Applied' : 'Saved'} {dateLabel}
            </span>
          )}
        </div>
      </div>

      {statusStr && <StatusPill status={statusStr} />}
      {tab === 'saved' && <BookmarkBorderOutlined sx={{ fontSize: 18, color: '#0094B3', flexShrink: 0 }} />}
    </div>
  );
}

export default function JobsCard({ animDelay = 0 }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const jobsResponse = useSelector((s) => s.jobs?.response ?? []);
  const counters = useSelector((s) => s.jobs?.counters ?? {});
  const pendingFlag = useSelector((s) => s.jobs?.pendingFlag ?? false);
  const pendingCounters = useSelector((s) => s.jobs?.pendingCounters ?? false);

  const [activeTab, setActiveTab] = useState('applied');
  const [savedList, setSavedList] = useState(null);
  const [appliedList, setAppliedList] = useState(null);
  const [query, setQuery] = useState('');

  const fetchTypeRef = useRef(null);
  const prevPending = useRef(false);

  useEffect(() => {
    const page = { active: 1, limit: jobsPageSize };
    dispatch(getCounters());

    fetchTypeRef.current = 'applied';
    dispatch(fetchAppliedJobs(page, {}, {}));
  }, []);

  useEffect(() => {
    if (prevPending.current && !pendingFlag) {
      if (fetchTypeRef.current === 'applied') {
        setAppliedList(jobsResponse);
        fetchTypeRef.current = 'saved';
        dispatch(fetchSavedJobs({ active: 1, limit: jobsPageSize }, {}, {}));
      } else if (fetchTypeRef.current === 'saved') {
        setSavedList(jobsResponse);
        fetchTypeRef.current = null;
      }
    }
    prevPending.current = pendingFlag;
  }, [pendingFlag, jobsResponse]);

  const savedCount = counters.SavedJobs ?? savedList?.length ?? 0;
  const appliedCount = counters.AppliedJobs ?? appliedList?.length ?? 0;

  const currentList = activeTab === 'applied' ? appliedList : savedList;
  const currentLoading = currentList === null;
  const preview = (currentList ?? []).slice(0, 3);

  const handleSearch = () => {
    navigate(`/dashboard/jobs?tab=publicjobs&searchTxt=${encodeURIComponent(query)}`);
  };

  const handleJobClick = (job) => {
    navigate(`/dashboard/jobs?tab=publicjobs&id=${job.Id}&page=1`);
  };

  const handleViewAll = () => {
    navigate(`/dashboard/jobs?tab=myjobs&subTab=${activeTab}`);
  };

  return (
    <DashboardCard noPadding animDelay={animDelay}>
      <CardHeader
        icon={<WorkOutline />}
        title="Jobs"
        action={
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/dashboard/jobs?tab=publicjobs')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate('/dashboard/jobs?tab=publicjobs');
            }}
            className="text-[14px] text-[#0094B3] font-medium cursor-pointer hover:text-[#0081A0] transition-colors"
          >
            Browse all →
          </span>
        }
      />

      <div className="flex gap-2.5 px-5 pb-3.5">
        <div className="relative flex-1">
          <SearchOutlined
            sx={{ fontSize: 17 }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7A85] pointer-events-none"
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Keyword — e.g. Commissioning Engineer"
            className="w-full border border-[#E2E8ED] rounded-[8px] pl-9 pr-3 py-2.5 text-[14px] text-[#1A2B35] bg-[#F8FAFB] outline-none focus:border-[#0094B3] focus:bg-white transition-all placeholder:text-[#6B7A85]"
          />
        </div>
        <button
          onClick={handleSearch}
          className="px-5 py-2.5 bg-[#022D41] text-white text-[14px] font-semibold rounded-[8px] border-none cursor-pointer hover:bg-[#0099CC] transition-colors shrink-0"
        >
          Search
        </button>
      </div>

      <div className="flex gap-2 px-5 pb-4">
        <Tab
          label="Applied"
          count={appliedCount}
          active={activeTab === 'applied'}
          loading={pendingCounters && appliedList === null}
          onClick={() => setActiveTab('applied')}
        />
        <Tab
          label="Saved"
          count={savedCount}
          active={activeTab === 'saved'}
          loading={pendingCounters && savedList === null}
          onClick={() => setActiveTab('saved')}
        />
      </div>

      <div className="border-t border-[#E2E8ED]">
        {currentLoading && [0, 1, 2].map((i) => <SkeletonRow key={i} />)}
        {!currentLoading && preview.length === 0 && (
          <EmptyState
            message={
              activeTab === 'applied'
                ? 'No applications yet — browse jobs to apply'
                : 'No saved jobs — bookmark roles you like'
            }
          />
        )}
        {!currentLoading &&
          preview.length !== 0 &&
          preview.map((job) => <JobRow key={job.Id} job={job} tab={activeTab} onClick={() => handleJobClick(job)} />)}
      </div>

      {currentList && currentList.length > 3 && (
        <button
          onClick={handleViewAll}
          className="w-full py-3 text-[14px] text-[#0094B3] font-medium bg-transparent border-none border-t border-[#E2E8ED] cursor-pointer hover:bg-[#F8FAFB] transition-colors"
        >
          Showing 3 of {currentList.length} · View all {activeTab} →
        </button>
      )}
    </DashboardCard>
  );
}
